import {IStore} from "modules/types/store";
import {createSelector} from "reselect";
import {getViewedEvent} from "modules/selectors/events";
import {getPlayersListForViewedEvent} from "modules/selectors/players";
import {defaultPlayers, LINEUP_SIZE} from "modules/constants";
import {filter, get, identity, keyBy, merge, size} from "lodash";
import {BoosterType, RequestState} from "modules/enums";

export const getTeamState = ({team: {hasUnsavedChanges, requestState}}: IStore) => ({
	hasUnsavedChanges,
	requestState,
});

export const getSuccessTeamSaveFlag = ({team}: IStore) => team.showSuccessSaveModal;

export const getTeamsByEventID = ({team}: IStore) => team.byEvent;

export const getViewedTeam = createSelector(
	getTeamsByEventID,
	getViewedEvent,
	(teamsByEventID, event): (typeof teamsByEventID)[number] | null =>
		teamsByEventID[event?.id || Number.MAX_SAFE_INTEGER] || null
);

export const getOldViewedTeamLineup = createSelector(
	getViewedTeam,
	getPlayersListForViewedEvent,
	(team, players) => {
		const emptyLineup = new Array<null>(LINEUP_SIZE).fill(null);
		const playersByID = keyBy(players, "id");
		const lineup = team?.lineup || [];
		const bench = team?.bench || [];
		const newLineup = [...lineup, ...bench];

		return merge(emptyLineup, newLineup).map((playerID) => playersByID[playerID!] || null);
	}
);

export const getViewedTeamLineup = createSelector(getViewedTeam, (team) => {
	if (!team) return defaultPlayers;

	return team?.players || [];
});

export const getPickedPlayersSize = createSelector(getViewedTeam, (team) =>
	size(filter(team?.players, ({playerID}) => identity(playerID)))
);

export const getIsTeamFull = createSelector(
	getPickedPlayersSize,
	(pickedPlayersSize) => pickedPlayersSize === LINEUP_SIZE
);

export const getModalPlayerID = ({team}: IStore) => team.showDetailsForPlayerID;
export const getLastPlayerIDCard = ({team}: IStore) => team.lastPlayerIDCard;
export const getIsTeamLoading = createSelector(
	getTeamState,
	(state) => state.requestState === RequestState.Requested
);

export const getTeamCaptainId = createSelector(getViewedTeam, (team) => team?.captain);

export const getIsCaptain = createSelector(getViewedTeam, (team) => (playerID?: number) => {
	if (!playerID) return false;

	return team?.captain === playerID;
});

export const getIsBenchPlayer = createSelector(
	getViewedTeam,
	(team) => (playerID: number) =>
		get(team, "players", []).findIndex((player) => playerID === player.playerID) > 3
);

export const getTeamBoosters = createSelector(getViewedTeam, (team) => get(team, "boosters", []));

export const getBoosterIsLoading = ({team}: IStore) =>
	team.boosterRequestState === RequestState.Requested;

export const getBoostersList = createSelector(
	getTeamBoosters,
	getViewedEvent,
	getBoosterIsLoading,
	(teamBoosters, event, isLoading) => {
		const boosters = [
			{
				title: "TRIPLE CAPTAIN",
				description: "Your captain will earn triple points!",
				type: BoosterType.TRIPLE_CAPTAIN,
			},
			{
				title: "BENCH BOOST",
				description: "The 2 players on your bench will also earn points!",
				type: BoosterType.BENCH_BOOST,
			},
		];

		const eventBooster = teamBoosters.find(({eventId}) => eventId === event?.id);

		return boosters.map((booster) => {
			const teamBooster = teamBoosters.find(({type}) => booster.type === type);

			return {
				...booster,
				isSelected: Boolean(teamBooster && teamBooster.eventId === event?.id),
				isUsed: Boolean(teamBooster && teamBooster.eventId !== event?.id),
				isAddDisabled: Boolean(eventBooster && eventBooster.type !== booster.type),
				id: teamBooster?.id,
				isLoading,
			};
		});
	}
);

export const getIsTripleCaptainActive = createSelector(
	getTeamBoosters,
	getViewedEvent,
	(boosters, event) =>
		boosters.some(
			(booster) =>
				event?.id === booster.eventId && booster.type === BoosterType.TRIPLE_CAPTAIN
		)
);

export const getIsBenchBoostActive = createSelector(
	getTeamBoosters,
	getViewedEvent,
	(boosters, event) =>
		boosters.some(
			(booster) => event?.id === booster.eventId && booster.type === BoosterType.BENCH_BOOST
		)
);

export const getIsTeamCreated = createSelector(getViewedTeam, (team) =>
	Boolean(team?.isTeamCreated)
);
